import { type ComboTradeResponse, type TradeResponse } from "#/types/orders";
import { type KyanError } from "#/types/service";
import { getIsDevMode } from "#/utils/getIsDevMode";
import { getQueryClient } from "#/utils/getQueryClient";
import { Logger } from "#/utils/logger";

import {
  CommonErrorType,
  type FetchResult,
  KyanErrorType,
} from "../shared/service.types";

export const defaultErrorHandler = (serviceName: string, error: KyanError) => {
  Logger.apiError(serviceName, error);

  if (error.errorType === KyanErrorType.SmartAccountNotFound) {
    throw new Error("Internal issue with address, contact sys admin.");
  }

  if (error.errorType === KyanErrorType.InternalServerError) {
    throw new Error(
      "Action failed. If the error persists, please contact support.",
    );
  }

  if (error.message.includes("Unable to find a signing key that matches")) {
    throw new Error(
      "1-CT Session Issue. If the error persists, please contact support.",
    );
  }

  throw new Error(
    error.message ||
      `API Error in ${serviceName} [${error.status}]. If the error persists, please contact support.`,
  );
};

const defaultOrderErrorHandler = ({
  serviceName,
  error,
}: {
  serviceName: string;
  error: KyanError | undefined;
}) => {
  if (!error) return;

  Logger.apiError(serviceName, error);

  if (error.errorType === KyanErrorType.InternalServerError) {
    throw new Error("Action failed. Check parameters and try again.");
  }

  if (error.errorType === KyanErrorType.LiquidityInsufficient) {
    throw new Error("Insufficient liquidity for this size and price.");
  }

  if (error.errorType === KyanErrorType.WashTrading) {
    throw new Error("Trade rejected: Wash trading!");
  }
};

const logAndThrowError = (
  serviceName: string,
  response: TradeResponse | ComboTradeResponse | undefined,
  message: string,
) => {
  Logger.apiResponse(serviceName, response);

  throw new Error(message);
};

type OrderErrorHandlerParams<T> =
  | { serviceName: string; error: KyanError; response?: never }
  | { serviceName: string; error?: never; response: T };

const marketOrderErrorHandler = ({
  serviceName,
  error,
}: OrderErrorHandlerParams<TradeResponse>) => {
  defaultOrderErrorHandler({ serviceName, error });
};

export const rfqOrderErrorHandler = ({
  serviceName,
  error,
  response,
}: OrderErrorHandlerParams<TradeResponse>) => {
  defaultOrderErrorHandler({ serviceName, error });

  if (!response) return;

  if (
    !response?.filledOrders.length ||
    response?.filledOrders[0].filled_amount === 0
  ) {
    logAndThrowError(
      serviceName,
      response,
      "Order failed, please try again. If error persists, contact support.",
    );
  }
};

export const comboOrderErrorHandler = ({
  serviceName,
  error,
  response,
}: OrderErrorHandlerParams<ComboTradeResponse>) => {
  defaultOrderErrorHandler({ serviceName, error });

  if (!response) return;

  if (
    !response?.filledOrders.length ||
    response?.filledOrders[0].filled_amount === 0
  ) {
    logAndThrowError(
      serviceName,
      response,
      "Order failed. Check parameters or liquidity.",
    );
  }
};

export const createMarketOrderEventHandlers = (serviceName: string) => {
  return {
    onSuccess: (response: TradeResponse) => {
      const isEmptyResponse =
        response?.fillResult.filled_amount === 0 &&
        response?.filledOrders.length === 0;

      if (!isEmptyResponse) return;

      logAndThrowError(
        serviceName,
        response,
        "Order failed, filled size is 0.",
      );
    },
    onError: (error: KyanError) => {
      marketOrderErrorHandler({ serviceName, error });
    },
  };
};

export interface DefaultApiResponseHandlerParams<Request, Response> {
  payload: Request;
  validation?: boolean[];
  serviceName: string;
  service: (payload: Request) => Promise<FetchResult<Response>>;
  onError?: (error: KyanError) => void;
  onSuccess?: (response: Response) => void;
}

export const defaultApiResponseHandler = async <Request, Response>({
  payload,
  validation,
  serviceName,
  service,
  onError,
  onSuccess,
}: DefaultApiResponseHandlerParams<Request, Response>) => {
  const isDevMode = getIsDevMode();
  const isOneClickTradingEnabled =
    payload !== null &&
    typeof payload === "object" &&
    "includeSessionHash" in payload;
  const compactServiceName = serviceName.replace(/\s+/g, "");

  if (isDevMode) {
    Logger.apiRequest(compactServiceName, payload, isOneClickTradingEnabled);

    if (validation) {
      Logger.apiValidation(compactServiceName, validation);
      if (validation.some((v) => !v)) {
        throw new Error(`Invalid signature in ${compactServiceName}`);
      }
    }
  }

  const result = await service(payload);

  if (!result) {
    throw new Error(`No result returned from service: ${compactServiceName}`);
  }

  const { data, error } = result;

  if (error) {
    if (
      error.errorType === KyanErrorType.SessionHashInvalid ||
      error.errorType === CommonErrorType.SessionNotFound
    ) {
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({ queryKey: ["isSessionValid"] });
    }

    onError?.(error);
    defaultErrorHandler(compactServiceName, error);
  }

  if (data === null || data === undefined) {
    throw new Error(`No data returned from service: ${compactServiceName}`);
  }

  if (isDevMode) {
    Logger.apiResponse(compactServiceName, data);
  }

  onSuccess?.(data);
  return data;
};
